import {
  ArrowBackOutlined,
  EditOutlined,
  InventoryOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  HelpOutlineOutlined,
  AddOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type {
  IBrandModel,
  IEquipment,
  PaginatedResponse,
} from "../../../types/equipment";
import AnimatedTable, { ColumnDef } from "./AnimatedTable";
import AddEquipmentDialog from "./AddEquipmentDialog";
import EditCatalogDialog from "./EditCatalogDialog";
import type { AddEquipmentFormData } from "../../../types/equipment";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  available: { label: "Available", color: "#4caf7d" },
  allocated: { label: "Allocated", color: "#5b9bd5" },
  rented: { label: "Rented", color: "#e09c4a" },
  maintenance: { label: "Maintenance", color: "#e05c5c" },
  lost: { label: "Lost", color: "#a07ad5" },
};

const CONDITION_CONFIG: Record<string, { label: string; color: string }> = {
  new: { label: "New", color: "#4caf7d" },
  good: { label: "Good", color: "#8bc34a" },
  fair: { label: "Fair", color: "#ffc107" },
  poor: { label: "Poor", color: "#ff7043" },
  damaged: { label: "Damaged", color: "#e05c5c" },
};

const statusChip = (raw?: string) => {
  const key = (raw || "available").toLowerCase();
  const cfg = STATUS_CONFIG[key] ?? { label: raw || "—", color: "#9e9e9e" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 600,
        borderRadius: "10px",
        backgroundColor: `${cfg.color}22`,
        color: cfg.color,
        border: `1px solid ${cfg.color}44`,
      }}
    />
  );
};

const conditionDot = (raw?: string) => {
  const key = (raw || "good").toLowerCase();
  const cfg = CONDITION_CONFIG[key] ?? { label: raw || "—", color: "#aaa" };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: cfg.color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          color: "text.secondary",
          textTransform: "capitalize",
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
};

const fmtDate = (d?: string) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  color,
  icon,
  colors,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
  icon?: React.ReactNode;
  colors: ReturnType<typeof tokens>;
}) => (
  <Box
    sx={{
      backgroundColor: colors.primary[800],
      borderRadius: "14px",
      px: 2.5,
      py: 2,
      minWidth: 120,
      flex: "1 1 120px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
      {icon && (
        <Box
          sx={{
            color: color ?? colors.primary[600],
            fontSize: 14,
            display: "flex",
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 11,
          color: colors.primary[700],
          textTransform: "uppercase",
          letterSpacing: 0.6,
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: color ?? colors.primary[100],
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ─── Spec row ─────────────────────────────────────────────────────────────────

const SpecRow = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary" }}>
        {value}
      </Typography>
    </Box>
  ) : null;

// ─── Page ─────────────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 10;

export default function CatalogDetailPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { brand_model_id } = useParams<{ brand_model_id: string }>();

  const [brandModel, setBrandModel] = useState<IBrandModel | null>(null);
  const [assets, setAssets] = useState<IEquipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<IEquipment | null>(null);
  const [editCatalogOpen, setEditCatalogOpen] = useState(false);

  // ── Fetchers ────────────────────────────────────────────────────────────────

  const fetchBrandModel = useCallback(async () => {
    try {
      const res = await apiService.get<IBrandModel>(
        `/api/equipments/brand-model/${brand_model_id}/`,
      );
      setBrandModel(res.data);
    } catch {
      setError("Failed to load catalog entry.");
    }
  }, [brand_model_id]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await apiService.get<PaginatedResponse<IEquipment>>(
        `/api/equipments/equipment/?page_size=1000`,
      );
      // Filter client-side by brand_model id
      const filtered = res.data.results.filter((eq) => {
        const bmId =
          eq.brand_model && typeof eq.brand_model === "object"
            ? (eq.brand_model as IBrandModel).id
            : eq.brand_model_id;
        return String(bmId) === String(brand_model_id);
      });
      setAssets(filtered);
    } catch {
      setError("Failed to load equipment assets.");
    }
  }, [brand_model_id]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([fetchBrandModel(), fetchAssets()]);
    setIsLoading(false);
  }, [fetchBrandModel, fetchAssets]);

  useEffect(() => {
    if (brand_model_id) fetchAll();
  }, [brand_model_id, fetchAll]);

  // ── Add / Edit asset ────────────────────────────────────────────────────────

  const handleSaveAsset = async (data: AddEquipmentFormData) => {
    try {
      const payload = {
        brand_model_id: brand_model_id,
        serial_number: data.unit.serial_number,
        asset_tag: data.unit.asset_tag || undefined,
        status: data.unit.status || "available",
        condition: data.unit.condition || "good",
        location: data.unit.location || undefined,
        description: data.unit.description || undefined,
        purchase_date: data.unit.purchase_date || undefined,
        warranty_expiry_date: data.unit.warranty_expiry_date || undefined,
        purchase_cost: data.unit.purchase_cost || undefined,
        current_value: data.unit.current_value || undefined,
        supplier_name: data.unit.supplier_name || undefined,
        contact_name: data.unit.contact_name || undefined,
        supplier_phone: data.unit.supplier_phone || undefined,
        supplier_email: data.unit.supplier_email || undefined,
        supplier_website: data.unit.supplier_website || undefined,
      };

      if (editingAsset) {
        await apiService.patch(
          `/api/equipments/equipment/${editingAsset.id}/`,
          payload,
        );
      } else {
        await apiService.post("/api/equipments/equipment/", payload);
      }

      setAddOpen(false);
      setEditingAsset(null);
      await fetchAll();
    } catch (err: unknown) {
      const flat = (o: unknown): string[] => {
        if (typeof o === "string") return [o];
        if (Array.isArray(o)) return o.flatMap(flat);
        if (o && typeof o === "object") return Object.values(o as Record<string, unknown>).flatMap(flat);
        return [];
      };
      const msgs = flat((err as { response?: { data?: unknown } })?.response?.data);
      setError(msgs.length ? msgs.join(" · ") : "Failed to save equipment.");
    }
  };

  // ── Derived stats ────────────────────────────────────────────────────────────

  const total = assets.length;
  const available = assets.filter(
    (a) => a.status?.toLowerCase() === "available",
  ).length;
  const allocated = assets.filter(
    (a) => a.status?.toLowerCase() === "allocated",
  ).length;
  const rented = assets.filter(
    (a) => a.status?.toLowerCase() === "rented",
  ).length;
  const maintenance = assets.filter(
    (a) => a.status?.toLowerCase() === "maintenance",
  ).length;
  const lost = assets.filter((a) => a.status?.toLowerCase() === "lost").length;

  // ── Table ────────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(assets.length / ROWS_PER_PAGE);
  const paginatedRows = assets.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const columns: ColumnDef<IEquipment>[] = [
    {
      key: "serial",
      label: "Serial / Tag",
      render: (eq) => (
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "text.primary",
              fontFamily: "monospace",
            }}
          >
            {eq.serial_number || "—"}
          </Typography>
          {eq.asset_tag && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {eq.asset_tag}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (eq) => statusChip(eq.status),
    },
    {
      key: "condition",
      label: "Condition",
      render: (eq) => conditionDot(eq.condition),
    },
    {
      key: "location",
      label: "Location",
      render: (eq) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
          {eq.location || "—"}
        </Typography>
      ),
    },
    {
      key: "purchase_date",
      label: "Purchased",
      render: (eq) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {fmtDate(eq.purchase_date)}
        </Typography>
      ),
    },
    {
      key: "warranty",
      label: "Warranty",
      render: (eq) => {
        if (!eq.warranty_expiry_date)
          return (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              —
            </Typography>
          );
        const expired = new Date(eq.warranty_expiry_date) < new Date();
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {expired ? (
              <WarningAmberOutlined sx={{ fontSize: 13, color: "#ffc107" }} />
            ) : (
              <CheckCircleOutlined sx={{ fontSize: 13, color: "#4caf7d" }} />
            )}
            <Typography
              sx={{
                fontSize: 12,
                color: expired ? "#ffc107" : "text.secondary",
              }}
            >
              {fmtDate(eq.warranty_expiry_date)}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "action",
      label: "",
      align: "center",
      render: (eq) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingAsset(eq);
              setAddOpen(true);
            }}
          >
            <EditOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ── Guards ───────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }

  if (!brandModel) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Catalog entry not found.</Typography>
      </Box>
    );
  }

  const categoryName =
    brandModel.category && typeof brandModel.category === "object"
      ? (brandModel.category as { name: string }).name
      : brandModel.category_name || "—";

  const hasSpecs =
    brandModel.length ||
    brandModel.height ||
    brandModel.width ||
    brandModel.weight ||
    brandModel.power_draw ||
    brandModel.current_draw;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* ── Top nav ─────────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ bgcolor: colors.primary[800] }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {brandModel.brand_name
                ? `${brandModel.brand_name} ${brandModel.model}`
                : brandModel.model}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {categoryName} · Catalog entry
            </Typography>
          </Box>

          <Tooltip title="Edit catalog entry">
            <IconButton
              onClick={() => setEditCatalogOpen(true)}
              sx={{ bgcolor: colors.primary[800], color: "text.primary" }}
            >
              <EditOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add new physical unit under this model">
            <IconButton
              onClick={() => {
                setEditingAsset(null);
                setAddOpen(true);
              }}
              sx={{
                bgcolor: colors.primary[400],
                color: colors.primary[900],
                "&:hover": { bgcolor: colors.primary[300] },
              }}
            >
              <AddOutlined />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Info + specs ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {/* Stock stats */}
          <Box
            sx={{
              flex: "2 1 400px",
              backgroundColor: colors.primary[900],
              borderRadius: "16px",
              p: 3,
            }}
          >
            {brandModel.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                mb={2.5}
                sx={{ maxWidth: 500 }}
              >
                {brandModel.description}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <StatCard
                label="Total"
                value={total}
                colors={colors}
                icon={<InventoryOutlined sx={{ fontSize: 14 }} />}
              />
              <StatCard
                label="Available"
                value={available}
                color="#4caf7d"
                colors={colors}
                icon={<CheckCircleOutlined sx={{ fontSize: 14 }} />}
              />
              <StatCard
                label="Allocated"
                value={allocated}
                color="#5b9bd5"
                colors={colors}
              />
              <StatCard
                label="Rented"
                value={rented}
                color="#e09c4a"
                colors={colors}
              />
              <StatCard
                label="Maintenance"
                value={maintenance}
                color="#e05c5c"
                colors={colors}
                icon={<BuildOutlined sx={{ fontSize: 14 }} />}
              />
              {lost > 0 && (
                <StatCard
                  label="Lost"
                  value={lost}
                  color="#a07ad5"
                  colors={colors}
                  icon={<HelpOutlineOutlined sx={{ fontSize: 14 }} />}
                />
              )}
            </Box>

            {brandModel.rental_value && (
              <Box sx={{ mt: 2.5, pt: 2 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Default rental rate
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: colors.primary[100],
                  }}
                >
                  ${brandModel.rental_value}
                  <Typography
                    component="span"
                    sx={{ fontSize: 13, color: "text.secondary", ml: 0.5 }}
                  >
                    / day
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>

          {/* Technical specs */}
          {hasSpecs && (
            <Box
              sx={{
                flex: "1 1 200px",
                backgroundColor: colors.primary[900],
                borderRadius: "16px",
                p: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.primary[700],
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  mb: 1.5,
                }}
              >
                Technical Specs
              </Typography>
              <SpecRow
                label={`Length (${brandModel.measuring_unit || "cm"})`}
                value={brandModel.length ?? undefined}
              />
              <SpecRow
                label={`Height (${brandModel.measuring_unit || "cm"})`}
                value={brandModel.height ?? undefined}
              />
              <SpecRow
                label={`Width (${brandModel.measuring_unit || "cm"})`}
                value={brandModel.width ?? undefined}
              />
              <SpecRow
                label="Weight (kg)"
                value={brandModel.weight ?? undefined}
              />
              <SpecRow
                label="Power Draw"
                value={brandModel.power_draw ?? undefined}
              />
              <SpecRow
                label="Current Draw"
                value={brandModel.current_draw ?? undefined}
              />
            </Box>
          )}
        </Box>

        {/* ── Assets table ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 3,
            minHeight: 200,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Gear Items
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {total} unit{total !== 1 ? "s" : ""} total
            </Typography>
          </Box>

          {assets.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <InventoryOutlined
                sx={{ fontSize: 36, color: colors.primary[600], mb: 1 }}
              />
              <Typography color="text.secondary" mb={2}>
                No physical units yet.
              </Typography>
              <Chip
                label="Add first unit"
                onClick={() => {
                  setEditingAsset(null);
                  setAddOpen(true);
                }}
                sx={{
                  cursor: "pointer",
                  backgroundColor: colors.primary[800],
                  color: "text.primary",
                  fontWeight: 600,
                }}
              />
            </Box>
          ) : (
            <AnimatedTable
              columns={columns}
              rows={paginatedRows}
              getRowId={(eq) => eq.id}
              onRowClick={(eq) => navigate(`/gear-management/view/${eq.id}`)}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyMessage="No units found."
            />
          )}
        </Box>
      </motion.div>

      {/* ── Edit catalog dialog ──────────────────────────────────────────────── */}
      {brandModel && (
        <EditCatalogDialog
          open={editCatalogOpen}
          onClose={() => setEditCatalogOpen(false)}
          brandModel={brandModel}
          onSaved={(updated) => {
            setBrandModel(updated);
            setEditCatalogOpen(false);
          }}
        />
      )}

      {/* ── Add / Edit asset dialog ───────────────────────────────────────────── */}
      <AddEquipmentDialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditingAsset(null);
        }}
        onSubmit={handleSaveAsset}
        equipment={editingAsset}
        isEdit={!!editingAsset}
        // Lock the brand model step to this catalog entry
        //lockedBrandModelId={String(brand_model_id)}
      />
    </Box>
  );
}
